/**
 * Night trains: what the leg card and the leg sheet say (spec
 * docs/product/night-trains/spec/night-trains-spec.md §7.1, §7.2, §7.7).
 * Pure copy builders over the backend's description of the train
 * (LegNightTrain, backend/services/nightTrains/legMode.js). Nothing here
 * adds a fact: every time, date, station and bed type comes from the data.
 *
 * Words (docs/product/night-trains/package/GLOSSARY.md): "Board at" and
 * "Get off at"; beds are "with beds" / "Beds on board" or "seats only"
 * everywhere except the leg sheet, which explains sleeper and couchette as
 * private and shared cabin;
 * a train is never a "journey". No em dashes (docs/codebase/ui-conventions.md).
 */
import type { LegNightTrain, NightTrainNearbyOption, NightTrainSearchSummary } from '../journeys/api';
import { isNightTrainMode } from '../../shared/transport';
import { formatDurationMin } from './utils';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const parse = (iso: string): Date => new Date(`${iso.slice(0, 10)}T00:00:00Z`);

/** "2026-10-20" → "Tue 20 Oct" */
export function fmtDay(iso: string): string {
  const d = parse(iso);
  return `${WEEKDAYS[d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
}

/** "2026-10-19" → "Mon 19" */
export function fmtDayShort(iso: string): string {
  const d = parse(iso);
  return `${WEEKDAYS[d.getUTCDay()]} ${d.getUTCDate()}`;
}

/** Days in a list, the month named once at the end: "Mon 19, Wed 21 Oct". */
export function fmtDays(isos: string[]): string {
  if (isos.length === 0) return '';
  const last = parse(isos[isos.length - 1]);
  const sameMonth = isos.every((i) => parse(i).getUTCMonth() === last.getUTCMonth());
  return sameMonth
    ? `${isos.map(fmtDayShort).join(', ')} ${MONTHS[last.getUTCMonth()]}`
    : isos.map(fmtDay).join(', ');
}

export function addDaysIso(iso: string, days: number): string {
  const d = parse(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** 818 → "13h38" (a timetable duration, exact). */
export function fmtRide(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  return `${Math.floor(m / 60)}h${String(m % 60).padStart(2, '0')}`;
}

/** "20:10 → 09:48" */
export function timesOnly(t: LegNightTrain): string {
  return `${t.boarding.time} → ${t.arrival.time}`;
}

/** "(13h38 travelling)" */
export function rideNote(t: LegNightTrain): string {
  return `(${fmtRide(t.durationMin)} travelling)`;
}

/** "20:10 → 09:48 (13h38 travelling)" (Paulo 2026-09-28, replacing the spec's "· 13h38"). */
export function timesLine(t: LegNightTrain): string {
  return `${timesOnly(t)} ${rideNote(t)}`;
}

/** "Nightjet NJ 409 · ÖBB" (the operator is left out when it is the product's own name). */
export function productLine(t: LegNightTrain): string {
  return t.operatorName && !t.displayName.includes(t.operatorName) ? `${t.displayName} · ${t.operatorName}` : t.displayName;
}

/** The mode table's chip (spec §7.3). The leg card says it in its first chip, see nightTrainChip. */
export function bedsChip(t: LegNightTrain): 'Beds on board' | 'Seats only' {
  return t.beds === 'beds' ? 'Beds on board' : 'Seats only';
}

/**
 * The leg card's first chip: the mode and what is on board in one, so the
 * card needs no separate beds chip (Paulo 2026-09-28). The package also has
 * night trains with seats only, and the card must say so.
 */
export function nightTrainChip(t: LegNightTrain): 'Night train with beds' | 'Night train, seats only' {
  return t.beds === 'beds' ? 'Night train with beds' : 'Night train, seats only';
}

/** Leg sheet only: what is on board, sleeper first, only what the train has. */
export function bedsList(t: LegNightTrain): string[] {
  return [
    t.hasSleeper ? 'Private cabin (sleeper)' : null,
    t.hasCouchette ? 'Shared cabin, 4 to 6 bunks (couchette)' : null,
    t.hasSeats ? 'Seats' : null,
  ].filter((x): x is string => Boolean(x));
}

/** The hedge for beds known only by the operator's general rule. */
export function bedsHedge(t: LegNightTrain): string | null {
  return t.bedsSource === 'operator_default' && t.beds === 'beds' ? 'Usually has beds, confirm when booking.' : null;
}

export function nightsOnBoardLabel(nights: number): string {
  return `${nights} ${nights === 1 ? 'night' : 'nights'} on board`;
}

export function savesLabel(nights: number): string {
  return nights === 1 ? 'saves a hotel night' : `saves ${nights} hotel nights`;
}

/** The leg card's second chip (Paulo 2026-09-28, replacing the spec's
 *  "1 night on board · saves a hotel night"). */
export function replacesLabel(nights: number): string {
  return nights === 1 ? 'Replaces a hotel night' : `Replaces ${nights} hotel nights`;
}

/**
 * "Runs nightly" or "Runs Mon, Wed, Fri". A line whose run dates follow no
 * weekly pattern has no weekdays to name (2 of the 746 direct trains between
 * the coverage analysis' cities): "Runs on selected dates".
 */
export function runsWord(t: Pick<LegNightTrain, 'weekdayLabel'>): string {
  if (t.weekdayLabel === 'Nightly') return 'Runs nightly';
  return t.weekdayLabel ? `Runs ${t.weekdayLabel}` : 'Runs on selected dates';
}

export interface RunsLine {
  text: string;
  /** warn: the traveller should act (not a run on the date, timetable changed). */
  tone: 'plain' | 'warn';
}

/**
 * When the train runs, for the leg's date (spec §7.2 "Runs on", §7.7).
 * Never "confirmed" beyond the published timetable, never "no night train".
 */
export function runsLine(t: LegNightTrain): RunsLine {
  if (t.timetableChanged) return { text: 'Timetable changed, check with the operator.', tone: 'warn' };
  if (!t.dateKnown || !t.date) {
    return {
      text: t.weekdayLabel === 'Nightly' ? 'Runs nightly.' : `${runsWord(t)}. Plan your dates around it.`,
      tone: 'plain',
    };
  }
  if (t.horizon) {
    const year = parse(t.date).getUTCFullYear() - 1;
    const notThen = t.expectedOnDate === false ? ` It did not run around this date in ${year}.` : '';
    return { text: `${runsWord(t)}. ${t.horizonLabel}.${notThen}`, tone: t.expectedOnDate === false ? 'warn' : 'plain' };
  }
  if (t.runsOnDate) {
    const next = t.nextRuns && t.nextRuns.length ? ` Next: ${fmtDays(t.nextRuns)}.` : '';
    return { text: `${runsWord(t)}.${next}`, tone: 'plain' };
  }
  const nearest = t.nearestRuns && t.nearestRuns.length ? ` Nearest: ${fmtDays(t.nearestRuns)}.` : '';
  return { text: `Not on ${fmtDay(t.date)}.${nearest}`, tone: 'warn' };
}

/** The short version for the leg card: only what needs saying there. */
export function cardRunsNote(t: LegNightTrain): RunsLine | null {
  if (t.timetableChanged) return runsLine(t);
  if (!t.dateKnown || !t.date) return { text: runsWord(t), tone: 'plain' };
  if (t.horizon) return { text: `${runsWord(t)} · ${t.horizonLabel}`, tone: t.expectedOnDate === false ? 'warn' : 'plain' };
  if (t.runsOnDate) return null; // the card's date tag already says the day
  return { text: `Not on ${fmtDay(t.date)}`, tone: 'warn' };
}

/** "Berlin Hbf · Tue 20 Oct, 20:48" (time only when the date is unknown). */
export function boardLine(t: LegNightTrain): string {
  const when = t.dateKnown && t.date ? `${fmtDay(t.date)}, ${t.boarding.time}` : t.boarding.time;
  return `${t.boarding.name} · ${when}`;
}

export function getOffLine(t: LegNightTrain): string {
  const offset = t.arrival.dayOffset ?? t.nightsOnBoard;
  const when = t.dateKnown && t.date
    ? `${fmtDay(addDaysIso(t.date, offset))}, ${t.arrival.time}`
    : `${t.arrival.time}${offset === 1 ? ', next day' : ` (+${offset} days)`}`;
  return `${t.arrival.name} · ${when}`;
}

/**
 * "33 km by road from Potsdam" when the station is not in the stop's own
 * town. By road where the map provider has the distance; else in a straight
 * line, and not called by road.
 */
export function stationDistanceNote(station: { tier: string; distanceKm: number; byRoad?: boolean }, stopCity: string): string | null {
  return station.tier === 'in_town' ? null : `${station.distanceKm} km${station.byRoad ? ' by road' : ''} from ${stopCity}`;
}

/** Trust mark tooltip: the data month. */
export function trustNote(t: LegNightTrain): string {
  const d = parse(t.dataDate);
  return `Timetable data from ${MONTHS_LONG[d.getUTCMonth()]} ${d.getUTCFullYear()}.`;
}

export function bookingLabel(t: LegNightTrain, where: 'card' | 'sheet'): string {
  const name = t.operatorName || t.productName;
  if (t.preferredChannel === 'omio') return where === 'card' ? 'Search tickets' : 'Check price on Omio';
  return where === 'card' ? `Book at ${name}` : `Check price and book at ${name}`;
}

/** Whether the night-train mode can be offered on a leg (backend:
 *  services/nightTrains/legMode.availabilityOf). 'unknown' = no timetable
 *  data for the area, which is never "no train". */
export type NightTrainStatus = 'available' | 'none' | 'too_short' | 'unknown';

const UNKNOWN_AREA = 'No timetable data for this area yet';

/** Why the pickers show "Night train" disabled (spec §7.3, and Paulo
 *  2026-09-28: the option stays visible, with its reason). */
export function unavailableReason(status: NightTrainStatus | null | undefined, note?: string | null): string {
  if (status === 'unknown') return note || UNKNOWN_AREA;
  return status === 'too_short' ? 'Too close for a night train' : 'No direct night train';
}

/** The mode table's line under the night-train row (spec §7.3): only the
 *  unknown state explains itself, "No timetable data for Spain yet." */
export function modeTableNote(status: NightTrainStatus | null | undefined, note?: string | null): string | null {
  return status === 'unknown' ? `${note || UNKNOWN_AREA}.` : null;
}

interface ModeOption {
  value: string;
  label: string;
  disabled?: boolean;
  hint?: string;
  /** The row does this instead of being picked (components/ui/Dropdown). */
  onSelect?: () => void;
  actionLabel?: string;
}

/** Said next to "Night train" where the data has a direct one for the leg. */
export const DIRECT_NIGHT_TRAIN_HINT = 'Direct night train available';

/**
 * The transport options of a leg's picker. "Night train" is always in the
 * list, right under Train, and says where the leg stands (Paulo 2026-09-29):
 *  - a direct night train exists: pickable, "Direct night train available";
 *  - none, or no data: the row searches instead ("No direct night train",
 *    then "Search night trains"), where a search is offered (`onSearch`);
 *  - the two places are too close: disabled, and says so.
 * A leg that is a night train already keeps the option as it is.
 */
export function modeOptionsForLeg(
  options: ModeOption[],
  leg: { status?: NightTrainStatus | null; note?: string | null; available?: boolean; current?: boolean; search?: NightTrainSearchSummary | null },
  onSearch?: () => void,
): ModeOption[] {
  const status: NightTrainStatus = leg.current || leg.available ? 'available' : (leg.status && leg.status !== 'available' ? leg.status : 'none');
  return options.map((o) => {
    if (String(o.value) !== 'night_train' || leg.current) return o;
    if (status === 'available') return { ...o, hint: DIRECT_NIGHT_TRAIN_HINT };
    const hint = unavailableReason(status, leg.note);
    if (onSearch && canSearchNearby(status)) {
      // The search would find nothing: the row says so, and does not search.
      const blocked = searchBlockedReason(leg.search);
      if (blocked) return { ...o, disabled: true, hint: blocked };
      return { ...o, disabled: false, hint, onSelect: onSearch, actionLabel: NEARBY_SEARCH_LABEL };
    }
    return { ...o, disabled: true, hint };
  });
}

/**
 * The search is offered on every part of the trip long enough for a night
 * train: 250 km in a straight line (Paulo 2026-09-29, after seeing it on
 * short ones). Under that the data has next to nothing: of the pairs of
 * cities 200 to 250 km apart 2% have a night train within 500 km, against
 * 20% at 250 to 300 km and 41% at 300 to 400 km
 * (docs/product/night-trains/README.md).
 */
export function canSearchNearby(status: NightTrainStatus | null | undefined): boolean {
  return status === 'available' || status === 'none' || status === 'unknown';
}

// ── Saved-alternative chip and trip summary (spec §7.6, backlog #53) ────────
// A long route is not one big hour count: the nights on board are said as
// nights, and only the rest as hours.

/** "2 night train rides" (the travel time, before the hours by day; Paulo
 *  2026-09-30: rides, not nights aboard). */
export function nightRidesLabel(rides: number): string {
  return `${rides} night train ${rides === 1 ? 'ride' : 'rides'}`;
}

/** Rides and hours by day from a journey's legs (the journey page has the
 *  legs, not a summary): a night-train leg is a ride; every other minute is
 *  by day. `minutesOf` gives a leg's minutes as the page counts them. */
export function travelTimeSplit<L extends { transportMode?: string | null; nightTrain?: unknown }>(
  legs: L[], minutesOf: (leg: L) => number,
): { rides: number; dayMinutes: number } {
  let rides = 0;
  let dayMinutes = 0;
  for (const l of legs) {
    const min = minutesOf(l);
    if (isNightTrainMode(l.transportMode) && l.nightTrain) rides += 1;
    else dayMinutes += Number.isFinite(min) ? min : 0;
  }
  return { rides, dayMinutes };
}

/** "−2 hotel nights" on the stay-cost pill, only where stays are priced. */
export function hotelNightsSavedLabel(nights: number): string {
  return `−${nights} hotel ${nights === 1 ? 'night' : 'nights'}`;
}

// ── The search before the search (Paulo 2026-09-29) ────────────────────────
// The backend says, for every part of the trip, what the search would find
// (NightTrainSearchSummary). The pages then say how many night trains there
// are and do not offer a search that finds none, so the traveller meets no
// dead end. The words say what was FOUND, never that there are none: the
// data does not know every night train.

/** "3 night trains", "5+ night trains"; on a part of the trip that is a night
 *  train already, "3 other night trains". Null when none was found or counted. */
export function searchCountLabel(search: NightTrainSearchSummary | null | undefined, other = false): string | null {
  if (!search || search.status !== 'found' || !(search.count > 0)) return null;
  const n = `${search.count}${search.more ? '+' : ''}`;
  return `${n} ${other ? 'other ' : ''}night ${search.count === 1 && !search.more ? 'train' : 'trains'}`;
}

/** Why the search is not offered, in the words the page shows in its place.
 *  Null: it is offered (also where nothing was worked out). */
export function searchBlockedReason(search: NightTrainSearchSummary | null | undefined, other = false): string | null {
  if (!search || search.status === 'found') return null;
  if (search.status === 'too_short') return unavailableReason('too_short');
  // No data for the countries of both places: never "no night trains".
  if (search.status === 'unknown') return search.note || UNKNOWN_AREA;
  const what = `No ${other ? 'other ' : ''}night trains found`;
  return search.status === 'none_on_dates' ? `${what} around your dates` : `${what} nearby`;
}

/** Whether the search can be opened for a part of the trip: its two places
 *  are far enough apart, and the search would find something (or nothing
 *  was worked out, and it may). */
export function searchOffered(leg: { transportMode?: string | null; nightTrainStatus?: NightTrainStatus | null; nightTrainSearch?: NightTrainSearchSummary | null }): boolean {
  const night = isNightTrainMode(leg.transportMode);
  if (!night && !canSearchNearby(leg.nightTrainStatus)) return false;
  return searchBlockedReason(leg.nightTrainSearch, night) == null;
}

// ── The night-train search (docs/product/night-trains/README.md) ───────────
// The words avoid timetable terms: a train "stops", it never "calls". And
// they promise nothing: "Search night trains", not "Night trains nearby",
// which says there are some (Paulo 2026-09-29).

/** The action, everywhere. */
export const NEARBY_SEARCH_LABEL = 'Search night trains';
/** On a part of the trip that is a night train already. */
export const NEARBY_SEARCH_OTHER_LABEL = 'Other night trains';

const byRoadWords = (byRoad?: boolean) => (byRoad ? ' by road' : '');

/** Every night train listed needs a change (none is boarded and left in the two
 *  places themselves): said above the list, so the list is not read as direct
 *  trains (Paulo 2026-09-30). */
export function nearbyNoDirectNote(from: string, to: string): string {
  return `No direct night train between ${from} and ${to}. The ones below stop nearby: you change there.`;
}

/** Nothing found. Never "no night trains": only none that fits, this close. */
export function nearbyEmptyNote(radiusKm: number, radiiKm: number[]): string {
  const wider = radiiKm.some((r) => r > radiusKm);
  return `No relevant night trains found within ${radiusKm} km of both places.${wider ? ' Try a wider search.' : ''}`;
}

/** The two places are too close for a night train. */
export function nearbyTooShortNote(from: string, to: string, legKm: number | null, minLegKm: number): string {
  const apart = legKm != null ? `${from} and ${to} are about ${Math.round(legKm / 10) * 10} km apart` : `${from} and ${to} are close to each other`;
  return `${apart}: too close for a night train. Night trains are shown for places ${minLegKm} km or more apart.`;
}

export interface NearbyRules {
  minLegKm: number;
  boardFrom: string;
  boardTo: string;
  getOffEarliest: string;
  minOnboardHours: number;
  minCoveredShare: number;
  maxPathRatio: number;
}

/** What it takes for a night train to be listed, in plain words. The numbers are the backend's. */
export function nearbyRules(r: NearbyRules): string[] {
  const until = r.boardTo === '24:00' ? 'midnight' : r.boardTo;
  const share = r.minCoveredShare === 0.5 ? 'half' : `${Math.round(r.minCoveredShare * 100)}%`;
  return [
    `The two places are ${r.minLegKm} km or more apart.`,
    'The train stops within the distance you chose of both places.',
    'It has beds on board.',
    `You board between ${r.boardFrom} and ${until}, and get off at ${r.getOffEarliest} or later, after ${r.minOnboardHours} hours or more on board.`,
    `It covers at least ${share} of the way, without a long detour.`,
    'It runs on your date or close to it.',
  ];
}

/** "290 km by road from Milan" */
/**
 * How far a station is from the trip's place: "290 km by road from Milan".
 * The option card shows it under a station that is not in town (amber when
 * the traveller changes there). By road where the map provider has the
 * distance, else in a straight line and not called by road.
 */
export function nearbyDistanceNote(km: number, city: string, byRoad?: boolean): string {
  return `${km} km${byRoadWords(byRoad)} from ${city}`;
}

/**
 * The stretch that is not by night train, where the traveller changes at the
 * station. The time is an estimate (by train) and is marked as one.
 */
export function nearbyChangeNote(side: 'boarding' | 'arrival', minutes: number | null, city: string): string {
  const time = minutes != null && Number.isFinite(minutes) && minutes > 0 ? `about ${formatDurationMin(minutes)}*` : null;
  if (side === 'boarding') return time ? `${time.replace(/^a/, 'A')} from ${city}, then change here` : `From ${city}, then change here`;
  return time ? `Change here, then ${time} to ${city}` : `Change here, then on to ${city}`;
}

/** A radius that finds nothing is not offered; without counts every radius is. */
export function radiusHasTrains(counts: Record<string, number> | undefined | null, radiusKm: number): boolean {
  return !counts || Number(counts[String(radiusKm)]) > 0;
}

/** One line of the review: a place of the trip, or how the traveller gets from one to the next. */
export type NearbyReviewStep =
  | { kind: 'place'; name: string; note: string | null; isNew: boolean }
  | { kind: 'travel'; night: boolean; text: string; note: string | null };

const dayStretch = (minutes: number | null): string => (
  minutes != null && Number.isFinite(minutes) && minutes > 0 ? `By train, about ${formatDurationMin(minutes)}*` : 'By train'
);

/**
 * The part of the trip as it will be with this night train, top to bottom,
 * for the traveller to review before adding it. A station that is far
 * becomes a new stop with 0 nights; a station that is near is where the
 * traveller boards or gets off, said under the place.
 */
export function nearbyReview(o: NightTrainNearbyOption, from: string, to: string): NearbyReviewStep[] {
  const steps: NearbyReviewStep[] = [];
  steps.push({ kind: 'place', name: from, isNew: false, note: null });
  if (o.changeAtBoarding) {
    steps.push({ kind: 'travel', night: false, text: dayStretch(o.toBoardingMinutes), note: null });
    // Named as the stop will be in the trip: the station's town.
    steps.push({ kind: 'place', name: o.boardingTown || o.train.boarding.name, isNew: true, note: 'New stop, 0 nights' });
  }
  steps.push({
    kind: 'travel',
    night: true,
    text: o.train.displayName,
    note: `${o.train.boarding.name} ${o.train.boarding.time} → ${o.train.arrival.name} ${o.train.arrival.time} ${rideNote(o.train)} · ${bedsChip(o.train)}`,
  });
  if (o.changeAtArrival) {
    steps.push({ kind: 'place', name: o.arrivalTown || o.train.arrival.name, isNew: true, note: 'New stop, 0 nights' });
    steps.push({ kind: 'travel', night: false, text: dayStretch(o.fromArrivalMinutes), note: null });
  }
  steps.push({ kind: 'place', name: to, isNew: false, note: null });
  return steps;
}

export interface NearbyReplaces {
  legs: Array<{ from: string; to: string; transportMode: string | null }>;
  /** Stops that came with the night train being replaced, and go with it. */
  stops: string[];
  hasUserData: boolean;
}

/** What the new night train takes the place of: "Zürich → Berlin by train". */
export function nearbyReplacesNote(r: NearbyReplaces, from: string, to: string, modeLabel: (mode: string | null) => string): string {
  const stops = r.stops.length ? `, with the stop in ${r.stops.join(' and ')}` : '';
  const mode = r.legs.length === 1 ? ` by ${modeLabel(r.legs[0].transportMode).toLowerCase()}` : '';
  return `${from} → ${to}${mode}${stops}`;
}

/** Said only when the traveller entered something on what is replaced. */
export const NEARBY_LOSS_NOTE = 'Ticket details and prices you entered for this part of the trip will be removed.';

export const NIGHT_TRAIN_TOOLTIP = 'Sleep on the train: board in the evening, wake up at your next stop. Saves a hotel night.';

/** Nights on board across a journey's legs, and how many night trains. */
export function nightTrainTotals(legs: Array<{ transportMode?: string | null; nightTrain?: { nightsOnBoard?: number } | null }>): { trains: number; nights: number } {
  let trains = 0;
  let nights = 0;
  for (const l of legs) {
    if (!l.nightTrain || !String(l.transportMode || '').toLowerCase().includes('night')) continue;
    trains += 1;
    nights += Math.max(1, Number(l.nightTrain.nightsOnBoard) || 1);
  }
  return { trains, nights };
}
